import { inject, injectable } from 'inversify'
import { Group, type Line, Matrix4 } from 'three'
import { setup } from 'xstate'
import type { IPoint2 } from '@/plugins/cad/module/common/interface'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import type { SelectorEventHandler } from '@/plugins/cad/module/core/selector/Selector'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from '../auxiliaryTool/auxiliaryMouseHelper'
import type { MouseTipHelper } from '../auxiliaryTool/MouseTipHelper'
import { MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { RotateEntityCommand } from '../command/RotateEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IEntity } from '../model/entity'
import type { EntitySelector } from '../selector/entitySelector'

const rotateEntityMachine = setup({
  actions: {
    entrySelectEntity: () => {},
    exitSelectEntity: () => {},
    entrySpecifyBasePoint: () => {},
    entrySpecifySecondPoint: () => {},
    entryCanceling: () => {},
  },
  guards: {
    isSelectedEntity: () => false,
  },
}).createMachine({
  id: 'rotateEntity',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [{ target: 'specifyBasePoint', guard: 'isSelectedEntity' }, 'selectEntity'],
      },
    },
    selectEntity: {
      entry: { type: 'entrySelectEntity' },
      exit: { type: 'exitSelectEntity' },
      on: {
        NEXT: 'specifyBasePoint',
        MOUSE_RIGHT_DOWN: [{ target: 'specifyBasePoint', guard: 'isSelectedEntity' }, 'canceling'],
      },
    },
    specifyBasePoint: {
      entry: { type: 'entrySpecifyBasePoint' },
      on: { NEXT: 'specifySecondPoint' },
    },
    specifySecondPoint: {
      entry: { type: 'entrySpecifySecondPoint' },
    },
    canceling: {
      entry: { type: 'entryCanceling' },
    },
  },
})

/**
 * 旋转实体工具：选中实体后指定旋转中心与参考点，按拖动角度绕中心旋转。
 */
@injectable()
export class RotateEntityTool
  extends StateMachineTool<typeof rotateEntityMachine>
  implements MouseEventInfoHandler, KeyEventInfoHandler, SelectorEventHandler<IEntity>
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.RotateEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = rotateEntityMachine.provide({
    actions: {
      entrySelectEntity: () => this.entrySelectEntity(),
      exitSelectEntity: () => this.exitSelectEntity(),
      entrySpecifyBasePoint: () => this.entrySpecifyBasePoint(),
      entrySpecifySecondPoint: () => this.entrySpecifySecondPoint(),
      entryCanceling: () => {
        this.exit()
      },
    },
    guards: {
      isSelectedEntity: () => this.isSelectedEntity(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private selectedEntities: IEntity[] = []
  /** 旋转中心（基准点） */
  private basePoint?: IPoint2
  /** 鼠标当前位置，用于计算角度 */
  private currentPoint?: IPoint2
  /** 按下基准点时记录的参考方向角 */
  private baseAngle: number = 0

  private group: Group = new Group()
  private leadLineObject?: Line
  private entityGroup?: Group

  protected onInitialize(): void {
    this.selectedEntities = this.entitySelector.getSelected()
    this.viewManager.attachTempObject(this.group)
    this.historyManager.setLock(true)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.leadLineObject && DrawUtils.disposeObject(this.leadLineObject)
    this.entityGroup && DrawUtils.disposeObject(this.entityGroup)
    this.group.parent?.remove(this.group)
    this.entitySelector.clearSelection()
    this.mouseTipHelper.removeTip(MouseTipId.Edit)
    this.historyManager.setLock(false)
    this.eject(this.auxiliaryMouseHelper)

    this.selectedEntities = []
    this.basePoint = undefined
    this.currentPoint = undefined
    this.baseAngle = 0
    this.leadLineObject = undefined
    this.entityGroup = undefined
  }

  /**
   * 鼠标按下：左键指定旋转中心或参考点，右键结束选择。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      if (this.checkState('specifyBasePoint')) {
        // 确定旋转中心
        this.basePoint = { ...params.worldPos }
        this.currentPoint = { ...params.worldPos }
        // 记录从基准点指向初始鼠标位置的参考方向角
        this.baseAngle = Math.atan2(
          params.worldPos.y - this.basePoint.y,
          params.worldPos.x - this.basePoint.x
        )
        this.send({ type: 'NEXT' })
        return
      }
    } else if (params.event.button === 2) {
      if (this.checkState('selectEntity')) {
        this.send({ type: 'MOUSE_RIGHT_DOWN' })
        return
      }
    }

    if (this.checkState('specifySecondPoint')) {
      // 左键/右键均可确认旋转
      this.executeOperate()
      this.exit()
    }
  }

  /**
   * 鼠标移动：基点/参考点阶段更新预览。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.checkState('specifyBasePoint') || this.checkState('specifySecondPoint')) {
      this.mouseCapture(params)
      this.update()
    }
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /**
   * 选择变化回调：记录待旋转实体并更新数量提示。
   *
   * @param entities - 当前已选中的实体
   */
  onSelectorChanged(entities: IEntity[]) {
    this.selectedEntities = entities
    this.mouseTipHelper.updateTip(MouseTipId.Edit, CadTipKey.edit.rotate.pendingCount, {
      count: this.selectedEntities.length,
    })
  }

  /************ 捕捉 ************/
  private mouseCapture(params: MouseEventParams) {
    this.currentPoint = this.auxiliaryMouseHelper.mouseCapture(params)
  }

  /************ 状态机状态处理 ************/
  private isSelectedEntity() {
    return this.selectedEntities.length > 0
  }

  private entrySelectEntity() {
    this.inject(this.entitySelector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.rotate.pendingCount, {
      count: this.selectedEntities.length,
    })
  }

  private exitSelectEntity() {
    this.eject(this.entitySelector)
  }

  private entrySpecifyBasePoint() {
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.rotate.basePoint)
    this.inject(this.auxiliaryMouseHelper)
  }

  private entrySpecifySecondPoint() {
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.rotate.angle)
  }

  /************ 预览渲染 ************/
  private update() {
    if (!this.basePoint || !this.currentPoint) {
      return
    }

    this.updateLeadLine({ startPoint: this.basePoint, endPoint: this.currentPoint })

    if (this.checkState('specifySecondPoint')) {
      const angle = this.computeAngle()
      this.updateEntityGroup(angle)
    }
  }

  /** 计算当前旋转角度（弧度） */
  private computeAngle(): number {
    if (!this.basePoint || !this.currentPoint) {
      return 0
    }
    const currentAngle = Math.atan2(
      this.currentPoint.y - this.basePoint.y,
      this.currentPoint.x - this.basePoint.x
    )
    return currentAngle - this.baseAngle
  }

  private updateEntityGroup(angle: number) {
    if (!this.basePoint) {
      return
    }
    if (!this.entityGroup) {
      this.entityGroup = new Group()
      this.group.add(this.entityGroup)
      this.selectedEntities.forEach((entity) => {
        const object = this.viewManager.getEntityObject(entity)
        const cloneObject = object?.clone()
        cloneObject && this.entityGroup!.add(cloneObject)
      })
    }
    // 绕基准点在 XY 平面旋转
    const { x, y } = this.basePoint
    const mat = new Matrix4()
      .makeTranslation(x, y, 0)
      .multiply(new Matrix4().makeRotationZ(angle))
      .multiply(new Matrix4().makeTranslation(-x, -y, 0))
    this.entityGroup.matrix.copy(mat)
    this.entityGroup.matrixAutoUpdate = false
  }

  private updateLeadLine(line: { startPoint: IPoint2; endPoint: IPoint2 }) {
    if (!this.leadLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(Setting.dashSize)
      this.leadLineObject = DrawUtils.drawDashedLine(
        line.startPoint,
        line.endPoint,
        {
          color: Setting.leadLineConfig.color,
          opacity: Setting.leadLineConfig.opacity,
          transparent: Setting.leadLineConfig.opacity < 1,
          dashSize,
          gapSize: dashSize / 2,
          depthTest: false,
          depthWrite: false,
        },
        this.group
      )
    } else {
      const geometry = DrawUtils.getBufferGeometryByPoints([line.startPoint, line.endPoint])
      this.leadLineObject.geometry = geometry
      this.leadLineObject.geometry.attributes.position.needsUpdate = true
      this.leadLineObject.computeLineDistances()
    }
  }

  /************ 数据处理 ************/
  private executeOperate() {
    if (!this.basePoint || !this.currentPoint) {
      return
    }
    const angle = this.computeAngle()
    const command = new RotateEntityCommand(
      this.selectedEntities,
      { center: this.basePoint, angle },
      this.viewManager
    )
    this.historyManager.addCommandAndExecute(command)
  }
}
